import * as React from "react";
import { withTheme } from "styled-components";

import type { Icon } from "common/components/icons";
import { getEmoji, definedMapWithDefault, defined, safeURL } from "common/Support";
import type { Loading } from "common/components/types";
import { LOADING } from "common/components/types";
import {
    SolidIconStyle,
    ListItemStyle,
    ListItemImageStyle,
    LinkCopyWrapper,
    SkeletonItem,
    EmojiDrawing,
    LinkArea,
} from "./ListItemStyle";
import type { RuntimeTheme } from "../../lib/theme/theme";
import type { ImageSource, DropImageHandler } from "common/components/portable-renderers";
import { firstImageURL } from "common/components/portable-renderers";
import type { MenuItem, ClickCallback } from "../../lib/menu-item";
import { getUrlHandling, UrlHandling } from "../../lib/link-utils";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import AppIcon from "common/app-icon";
import { trackLinkClick } from "../../../analytics/link-clicks";

interface Props {
    text?: string | Loading;
    detail?: string | Loading;
    imageSource?: ImageSource;
    fallbackInitials?: string;
    interactive?: boolean;
    onClick?: OnClickHandler;
    targetUrl?: string;
    onDropImage?: DropImageHandler;
    allowWrapping: boolean;
    theme: RuntimeTheme;
    menuItems?: MenuItem[];
    style?: "normal" | "inverted";
}

interface State {
    touched: boolean;
}

class ListItem extends React.Component<Props, State> {
    public state: State = { touched: false };

    private onTouchStart = (_ev: React.TouchEvent<HTMLDivElement>) => {
        this.setState({ touched: true });
    };

    private onTouchEnd = (_ev: React.TouchEvent<HTMLDivElement>) => {
        this.setState({ touched: false });
    };

    private onClick = async (_ev: React.MouseEvent<HTMLDivElement>) => {
        if (this.props.onClick !== undefined) {
            await this.props.onClick(false);
        }
    };

    private onLinkClick = async (handled: boolean) => {
        if (this.props.onClick !== undefined) {
            await this.props.onClick(handled);
        }
    };

    public shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>): boolean {
        if (
            nextProps.text !== this.props.text ||
            nextProps.detail !== this.props.detail ||
            nextProps.imageSource !== this.props.imageSource ||
            nextProps.fallbackInitials !== this.props.fallbackInitials ||
            nextProps.targetUrl !== this.props.targetUrl ||
            nextProps.theme !== this.props.theme ||
            nextState.touched !== this.state.touched
        ) {
            // OnClick can vary, output wont change
            return true;
        }

        if (nextProps.menuItems !== this.props.menuItems) {
            const nextCount = definedMapWithDefault(nextProps.menuItems, 0, mi => mi.length);
            const thisCount = definedMapWithDefault(this.props.menuItems, 0, mi => mi.length);

            if (nextCount !== thisCount) {
                return true;
            }
        }

        return false;
    }

    public render(): React.ReactNode {
        const { text, detail, imageSource, fallbackInitials, interactive } = this.props;
        const style = this.props.style ?? "normal";
        let menuItems = this.props.menuItems;

        const isInteractive = interactive === true;
        let navigation = false;
        if (isInteractive && menuItems === undefined) {
            menuItems = [
                {
                    title: "chevron",
                    icon: "chevron-right",
                },
            ];
            navigation = true;
        }

        const textStyle = style === "normal" ? "textStyle topItem" : "textStyle";
        const detailStyle = style === "normal" ? "textDetailStyle" : "textDetailStyle topItem";

        let textContent: React.ReactNode;
        if (text === LOADING) {
            textContent = <SkeletonItem height="16px" width="80%" marginBottom="4" />;
        } else {
            textContent = <div className={textStyle}>{text}</div>;
        }

        const isLink = this.props.targetUrl !== undefined;

        let imgUrl: string | undefined;
        if (imageSource !== undefined) {
            imgUrl = firstImageURL(imageSource, { thumbnail: true });
        }

        let iconNode: React.ReactNode;

        if (menuItems !== undefined) {
            iconNode = menuItems.map(mi => (
                <ListItemIcon
                    key={mi.title}
                    theme={this.props.theme}
                    icon={mi.icon}
                    navigation={navigation}
                    href={mi.href}
                    callback={mi.onClick}
                />
            ));
        }

        let linkArea: React.ReactNode;
        const href = safeURL(this.props.targetUrl);

        if (href !== undefined) {
            const handling = getUrlHandling(href, false);
            const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
            const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;
            linkArea = (
                <LinkArea
                    onClick={async () => {
                        trackLinkClick(href);
                        await this.onLinkClick(useTag);
                    }}
                    href={useTag ? href : undefined}
                    target={target}
                />
            );
        }

        return (
            <ListItemStyle
                touched={this.state.touched}
                interactive={isInteractive}
                onClick={isLink ? undefined : this.onClick}
                allowWrapping={this.props.allowWrapping}
                onTouchStart={this.onTouchStart}
                onTouchEnd={this.onTouchEnd}
                onTouchCancel={this.onTouchEnd}>
                {linkArea}
                <ListItemImage onDropImage={this.props.onDropImage} src={imgUrl} initials={fallbackInitials} />
                <div className="textContainer">
                    {style === "normal" && textContent}
                    {detail === LOADING && <SkeletonItem height="16px" />}
                    {detail !== LOADING && <div className={detailStyle}>{detail}</div>}
                    {style === "inverted" && textContent}
                </div>
                {iconNode}
            </ListItemStyle>
        );
    }
}

function ListItemIcon({
    icon,
    navigation,
    theme,
    href,
    callback,
}: {
    icon: Icon;
    navigation: boolean;
    theme: RuntimeTheme;
    href?: string;
    callback?: ClickCallback;
}): JSX.Element {
    if (navigation) {
        return (
            <div className="navigationIcon">
                <AppIcon icon={icon} size={24} />
            </div>
        );
    }

    const result = (
        <SolidIconStyle isLast={true}>
            <AppIcon icon={icon} size={theme.listItem.actionImage.iconSize} />
        </SolidIconStyle>
    );
    href = safeURL(href);
    if (href === undefined || callback === undefined) {
        return result;
    }
    // FIXME: This callbacks should not require an href implicitly.
    const handling = getUrlHandling(href, false);
    const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
    const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;
    return (
        <LinkCopyWrapper
            onClick={ev => {
                trackLinkClick(defined(href));
                if (useTag) {
                    callback(true);
                } else {
                    callback(false);
                }
                ev.stopPropagation();
            }}
            href={useTag ? href : undefined}
            target={target}>
            {result}
        </LinkCopyWrapper>
    );
}

interface ImageProps {
    src?: string;
    initials?: string;
    onDropImage?: DropImageHandler;
}

const ListItemImage = ({ src, initials, onDropImage }: ImageProps) => {
    const fallback = <div className="imageBaseStyle initialsStyle">{initials}</div>;
    if (src !== undefined) {
        const emoji = getEmoji(src);
        if (emoji !== undefined) {
            return (
                <div className="emojiImageStyle">
                    <EmojiDrawing str={emoji} />
                </div>
            );
        } else {
            return <ListItemImageStyle onDropImage={onDropImage} src={src} alternate={fallback} altBehavior="both" />;
        }
    } else if (initials !== undefined) {
        return <ListItemImageStyle onDropImage={onDropImage} alternate={fallback} altBehavior="both" />;
    } else {
        // Nothing is rendered if we have neither src nor initials
        return null;
    }
};

export default withTheme(ListItem);
